import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LogOut, Shield, Smartphone, HelpCircle, ChevronRight, 
  User, Phone, Mail, Lock, X, Bell, Check, HelpCircle as HelpIcon, 
  MessageSquare, Globe, Loader2, Save, KeyRound, Calendar, FileText
} from 'lucide-react';
import { formatCPF } from '../../utils/cpfUtils';
import { useAuthStore } from '../../store/authStore';
import { useAvatarUpload } from '../../hooks/useAvatarUpload';
import { UserAvatar } from '../../components/ui/UserAvatar';
import { useAppVersionTelemetry } from '../../features/telemetry/useAppVersionTelemetry';
import { AuditTelemetryModal } from '../../features/telemetry/AuditTelemetryModal';
import { toast } from 'sonner';

const API = import.meta.env.VITE_API_URL || '/api/v1';

export function MobilePerfil() {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user) || {} as any;
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const token = localStorage.getItem('ac_token');
  const telemetry = useAppVersionTelemetry();
  const [auditModalOpen, setAuditModalOpen] = useState(false);

  const {
    isUploading: uploadingPhoto,
    openFilePicker: handleFotoClick,
    fileInputRef,
    handleInputChange: handleFotoChange,
    cropperModal,
  } = useAvatarUpload();

  // Modal control states
  const [activeModal, setActiveModal] = useState<'edit' | 'security' | 'notifications' | 'help' | null>(null);

  // Form states
  const [nome, setNome] = useState(user.nome || '');
  const [telefone, setTelefone] = useState(user.telefone || '');
  
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');

  // Notifications preferences
  const [prefWhatsApp, setPrefWhatsApp] = useState(user.preferencias?.notificacoes?.whatsapp ?? true);
  const [prefPush, setPrefPush] = useState(user.preferencias?.notificacoes?.push ?? true);
  const [prefCom, setPrefCom] = useState(user.preferencias?.notificacoes?.comunicados ?? true);

  // Loading states
  const [loading, setLoading] = useState(false);

  // FAQ Accordion state
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const roleLabel: Record<string, string> = {
    ADMIN: 'Administrador',
    ESCOLA: 'Diretor / Gestão',
    PROFESSOR: 'Professor',
    RESPONSAVEL: 'Responsável',
    ALUNO: 'Aluno',
    MUNICIPAL: 'Gestor Municipal',
    ESTADUAL: 'Gestor Estadual',
    REDE: 'Gestor de Rede',
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSavePerfil = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast.error('O nome não pode ficar em branco.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ nome: nome.trim(), telefone: telefone.trim() })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Erro ao atualizar perfil.');
      }

      await checkAuth();
      toast.success('Perfil atualizado com sucesso!');
      setActiveModal(null);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao conectar ao servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!novaSenha) {
      toast.error('Informe a nova senha.');
      return;
    }
    if (novaSenha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      toast.error('As senhas não conferem.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/me/password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ senha: novaSenha, senhaAtual })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Erro ao atualizar senha.');
      }

      toast.success('Senha alterada com sucesso!');
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
      setActiveModal(null);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao conectar ao servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotificacoes = async () => {
    setLoading(true);
    try {
      const preferencias = {
        ...(user.preferencias || {}),
        notificacoes: {
          ...(user.preferencias?.notificacoes || {}),
          whatsapp: prefWhatsApp,
          push: prefPush,
          comunicados: prefCom,
        }
      };

      const res = await fetch(`${API}/auth/me/preferencias`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(preferencias)
      });

      if (!res.ok) {
        throw new Error('Erro ao salvar preferências.');
      }

      await checkAuth();
      toast.success('Preferências de notificação salvas!');
      setActiveModal(null);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao conectar ao servidor.');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (val?: string) => {
    if (!val) return '';
    const clean = val.replace(/\D/g, '');
    if (clean.length === 11) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
    }
    if (clean.length === 10) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
    }
    return val;
  };

  const openEditModal = () => {
    setNome(user.nome || '');
    setTelefone(user.telefone || '');
    setActiveModal('edit');
  };

  return (
    <div className="m-animate" style={{ position: 'relative', paddingBottom: 24 }}>
      
      {/* ─── Profile Header Card ─── */}
      <div className="m-card" style={{ 
        position: 'relative', 
        padding: '24px 20px', 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        overflow: 'hidden'
      }}>
        {/* Glow de fundo */}
        <div style={{
          position: 'absolute', top: -30, right: -30, width: 90, height: 90,
          background: 'radial-gradient(circle, rgba(124,58,237,0.08), transparent 70%)',
        }} />

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <UserAvatar
            src={user?.fotoUrl}
            name={user?.nome}
            role={user?.perfil || user?.role}
            size="xl"
            editable
            isUploading={uploadingPhoto}
            onEditClick={handleFotoClick}
            onClick={handleFotoClick}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFotoChange}
          />
          {cropperModal}
        </div>

        <div style={{ textAlign: 'center' }}>
          <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>
            {user.nome || 'Usuário'}
          </h3>
          <span className="m-badge" style={{ 
            background: '#ede9fe', 
            color: '#7c3aed', 
            fontSize: '0.72rem', 
            padding: '2px 8px',
            borderRadius: 6,
            fontWeight: 700
          }}>
            {roleLabel[user.perfil || user.role] || user.perfil || user.role || 'Perfil'}
          </span>
          <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Mail size={12} /> {user.email || ''}
          </p>
          {(user.cpf || user.responsavelVinculado?.cpf) && (
            <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <FileText size={12} /> CPF: {formatCPF(user.cpf || user.responsavelVinculado?.cpf)}
            </p>
          )}
          {(user.dataNascimento || user.responsavelVinculado?.dataNascimento) && (
            <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Calendar size={12} /> {new Date(user.dataNascimento || user.responsavelVinculado?.dataNascimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
            </p>
          )}
          {(user.telefone || user.responsavelVinculado?.telefone) && (
            <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Phone size={12} /> {formatPhone(user.telefone || user.responsavelVinculado?.telefone)}
            </p>
          )}

          <button 
            onClick={openEditModal}
            style={{
              marginTop: 16,
              padding: '8px 16px',
              borderRadius: 10,
              background: '#7c3aed',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 3px 8px rgba(124, 58, 237, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <User size={14} />
            Editar Meus Dados
          </button>
        </div>
      </div>

      {/* ─── Menu Options ─── */}
      <p className="m-section-title" style={{ marginTop: 22 }}>Configurações do Aplicativo</p>
      
      {/* Option: Segurança */}
      <div 
        className="m-card" 
        onClick={() => setActiveModal('security')}
        style={{ padding: '14px 16px', cursor: 'pointer', transition: 'transform 0.1s active', border: '1px solid #f1f5f9' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="m-card-icon" style={{ background: '#eef2ff', color: '#4f46e5', width: 38, height: 38, borderRadius: 12 }}>
            <Shield size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>Segurança</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Mudar senha da conta</div>
          </div>
          <ChevronRight size={16} color="#94a3b8" />
        </div>
      </div>

      {/* Option: Notificações */}
      <div 
        className="m-card" 
        onClick={() => setActiveModal('notifications')}
        style={{ padding: '14px 16px', cursor: 'pointer', border: '1px solid #f1f5f9' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="m-card-icon" style={{ background: '#ecfdf5', color: '#059669', width: 38, height: 38, borderRadius: 12 }}>
            <Smartphone size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>Notificações</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Canais e preferências de alertas</div>
          </div>
          <ChevronRight size={16} color="#94a3b8" />
        </div>
      </div>

      {/* Option: Ajuda & Suporte */}
      <div 
        className="m-card" 
        onClick={() => setActiveModal('help')}
        style={{ padding: '14px 16px', cursor: 'pointer', border: '1px solid #f1f5f9' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="m-card-icon" style={{ background: '#fff7ed', color: '#ea580c', width: 38, height: 38, borderRadius: 12 }}>
            <HelpCircle size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>Ajuda & Suporte</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Dúvidas frequentes e canal de suporte</div>
          </div>
          <ChevronRight size={16} color="#94a3b8" />
        </div>
      </div>

      {/* ─── Logout Button ─── */}
      <button
        onClick={handleLogout}
        style={{
          width: '100%',
          padding: 14,
          marginTop: 20,
          border: '1.5px dashed #fecaca',
          borderRadius: 16,
          background: '#fef2f2',
          color: '#dc2626',
          fontWeight: 700,
          fontSize: '0.88rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          transition: 'all 0.2s'
        }}
      >
        <LogOut size={18} />
        Sair da conta
      </button>

      {/* ─── Widget de Homologação & Telemetria Mobile ─── */}
      <div
        onClick={() => setAuditModalOpen(true)}
        role="button"
        tabIndex={0}
        style={{
          marginTop: 24,
          marginBottom: 16,
          padding: '12px 14px',
          borderRadius: 14,
          background: '#0f172a',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          transition: 'all 0.15s ease',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              background: '#2563eb',
              color: '#ffffff',
              padding: '2px 7px',
              borderRadius: 6,
              fontSize: '0.72rem',
              fontWeight: 800,
              fontFamily: 'monospace',
            }}>
              {telemetry.localManifest.version}
            </span>
            <span style={{
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#94a3b8',
              padding: '2px 6px',
              borderRadius: 6,
              fontSize: '0.68rem',
              fontFamily: 'monospace',
            }}>
              {telemetry.localManifest.gitCommit}
            </span>
            <span style={{
              background: 'rgba(99, 102, 241, 0.15)',
              color: '#a5b4fc',
              padding: '2px 6px',
              borderRadius: 6,
              fontSize: '0.68rem',
              fontFamily: 'monospace',
            }}>
              {location.pathname}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: telemetry.isStale ? '#ef4444' : '#10b981',
              boxShadow: telemetry.isStale ? '0 0 6px #ef4444' : '0 0 6px #10b981',
            }} />
            <span style={{
              fontSize: '0.66rem',
              fontWeight: 700,
              color: telemetry.isStale ? '#fca5a5' : '#86efac',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>
              {telemetry.isStale ? 'OBSOLETO' : 'HOMOLOGAÇÃO: SINCRONIZADO'}
            </span>
          </div>
        </div>

        {telemetry.currentRouteMeta.trelloCard && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.04)',
            padding: '6px 10px',
            borderRadius: 8,
            fontSize: '0.68rem',
            color: '#cbd5e1',
            lineHeight: 1.35,
            borderLeft: '3px solid #818cf8',
          }}>
            📋 <strong>Demanda:</strong> {telemetry.currentRouteMeta.trelloCard}
          </div>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          paddingTop: 8,
          flexWrap: 'wrap',
        }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setAuditModalOpen(true);
            }}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid rgba(168, 85, 247, 0.4)',
              background: 'rgba(147, 51, 234, 0.2)',
              color: '#d8b4fe',
              fontSize: '0.68rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            🐞 Ver Logs & Diagnóstico
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void telemetry.purgeAndReload();
            }}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid rgba(129, 140, 248, 0.3)',
              background: 'rgba(99, 102, 241, 0.15)',
              color: '#c7d2fe',
              fontSize: '0.68rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            🔄 Limpar Cache / Atualizar
          </button>
        </div>
      </div>

      {/* Modal de Auditoria e Limpeza */}
      <AuditTelemetryModal
        open={auditModalOpen}
        onClose={() => setAuditModalOpen(false)}
        telemetry={telemetry}
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ─── MODALS OVERLAY (CSS overlay sliding up from bottom) ─── */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {activeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          zIndex: 9999,
        }} onClick={() => setActiveModal(null)}>
          
          <div style={{
            width: '100%',
            maxWidth: 480,
            backgroundColor: '#ffffff',
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            padding: '24px 20px',
            maxHeight: '85dvh',
            overflowY: 'auto',
            boxShadow: '0 -10px 25px -5px rgba(0, 0, 0, 0.1), 0 -8px 10px -6px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 20
          }} onClick={e => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {activeModal === 'edit' && <User size={20} style={{ color: '#7c3aed' }} />}
                {activeModal === 'security' && <Shield size={20} style={{ color: '#4f46e5' }} />}
                {activeModal === 'notifications' && <Bell size={20} style={{ color: '#059669' }} />}
                {activeModal === 'help' && <HelpIcon size={20} style={{ color: '#ea580c' }} />}
                
                <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  {activeModal === 'edit' && 'Editar Dados'}
                  {activeModal === 'security' && 'Segurança da Conta'}
                  {activeModal === 'notifications' && 'Preferências de Alerta'}
                  {activeModal === 'help' && 'Ajuda & Suporte'}
                </h4>
              </div>
              <button 
                onClick={() => setActiveModal(null)}
                style={{ background: '#f1f5f9', border: 'none', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            
            {/* CASE 1: EDIT PROFILE */}
            {activeModal === 'edit' && (
              <form onSubmit={handleSavePerfil} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                    Nome Completo
                  </label>
                  <input 
                    type="text" 
                    value={nome}
                    onChange={e => setNome(e.target.value)}
                    required
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                    Telefone de Contato
                  </label>
                  <input 
                    type="text" 
                    value={telefone}
                    placeholder="(00) 00000-0000"
                    onChange={e => setTelefone(formatPhone(e.target.value))}
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none'
                    }}
                  />
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%', padding: 14, borderRadius: 12, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8, opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                  Salvar Alterações
                </button>
              </form>
            )}

            {/* CASE 2: SECURITY & PASSWORD */}
            {activeModal === 'security' && (
              <form onSubmit={handleSaveSenha} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 6px', lineHeight: 1.4 }}>
                  Para alterar sua senha, preencha a sua senha atual e digite a nova senha desejada abaixo.
                </p>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                    Senha Atual
                  </label>
                  <input 
                    type="password" 
                    value={senhaAtual}
                    onChange={e => setSenhaAtual(e.target.value)}
                    placeholder="Sua senha atual"
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                    Nova Senha
                  </label>
                  <input 
                    type="password" 
                    value={novaSenha}
                    onChange={e => setNovaSenha(e.target.value)}
                    placeholder="Mínimo de 8 caracteres"
                    required
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                    Confirmar Nova Senha
                  </label>
                  <input 
                    type="password" 
                    value={confirmarSenha}
                    onChange={e => setConfirmarSenha(e.target.value)}
                    placeholder="Repita a nova senha"
                    required
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none'
                    }}
                  />
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%', padding: 14, borderRadius: 12, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8, opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <KeyRound size={16} />}
                  Atualizar Senha
                </button>
              </form>
            )}

            {/* CASE 3: NOTIFICATIONS */}
            {activeModal === 'notifications' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0, lineHeight: 1.4 }}>
                  Personalize quais alertas você deseja receber no seu celular sobre a atividade do(s) seu(s) filho(s).
                </p>

                {/* Toggle: WhatsApp */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>Mensagens por WhatsApp</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Envio de alertas de entrada e saída no WhatsApp</div>
                  </div>
                  <label className="m-switch-container" style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                    <input 
                      type="checkbox" 
                      checked={prefWhatsApp} 
                      onChange={e => setPrefWhatsApp(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: prefWhatsApp ? '#059669' : '#cbd5e1',
                      borderRadius: 24, transition: '0.3s'
                    }}>
                      <span style={{
                        position: 'absolute', content: '""', height: 18, width: 18, left: prefWhatsApp ? 22 : 3, bottom: 3,
                        backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                      }} />
                    </span>
                  </label>
                </div>

                {/* Toggle: Push Notifications */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>Notificações Push PWA</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Receber alertas na barra do celular em tempo real</div>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                    <input 
                      type="checkbox" 
                      checked={prefPush} 
                      onChange={e => setPrefPush(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: prefPush ? '#059669' : '#cbd5e1',
                      borderRadius: 24, transition: '0.3s'
                    }}>
                      <span style={{
                        position: 'absolute', content: '""', height: 18, width: 18, left: prefPush ? 22 : 3, bottom: 3,
                        backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                      }} />
                    </span>
                  </label>
                </div>

                {/* Toggle: Comunicados Escolares */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>Boletins e Comunicados</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Notificar quando a diretoria enviar novos avisos</div>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                    <input 
                      type="checkbox" 
                      checked={prefCom} 
                      onChange={e => setPrefCom(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: prefCom ? '#059669' : '#cbd5e1',
                      borderRadius: 24, transition: '0.3s'
                    }}>
                      <span style={{
                        position: 'absolute', content: '""', height: 18, width: 18, left: prefCom ? 22 : 3, bottom: 3,
                        backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                      }} />
                    </span>
                  </label>
                </div>

                <button 
                  onClick={handleSaveNotificacoes}
                  disabled={loading}
                  style={{
                    width: '100%', padding: 14, borderRadius: 12, border: 'none', background: '#059669', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12, opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={16} />}
                  Salvar Preferências
                </button>
              </div>
            )}

            {/* CASE 4: HELP & SUPPORT */}
            {activeModal === 'help' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0, lineHeight: 1.4 }}>
                  Tire suas dúvidas rápidas ou fale diretamente com a equipe do Aluno Conectado.
                </p>

                {/* FAQ list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  
                  {/* FAQ 1 */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                    <button 
                      onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                      style={{ width: '100%', padding: '12px 14px', background: '#f8fafc', border: 'none', textAlign: 'left', fontWeight: 700, fontSize: '0.8rem', color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    >
                      <span>Como funciona o registro de presença?</span>
                      <ChevronRight size={14} style={{ transform: openFaq === 0 ? 'rotate(90deg)' : 'none', transition: '0.2s' }} />
                    </button>
                    {openFaq === 0 && (
                      <div style={{ padding: '12px 14px', fontSize: '0.75rem', color: '#475569', lineHeight: 1.5, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
                        A chamada é realizada em sala de aula pelo professor. Assim que ele conclui o lançamento, o sistema processa os dados e dispara um push instantâneo informando se seu filho está presente ou ausente.
                      </div>
                    )}
                  </div>

                  {/* FAQ 2 */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                    <button 
                      onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                      style={{ width: '100%', padding: '12px 14px', background: '#f8fafc', border: 'none', textAlign: 'left', fontWeight: 700, fontSize: '0.8rem', color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    >
                      <span>Posso justificar faltas pelo aplicativo?</span>
                      <ChevronRight size={14} style={{ transform: openFaq === 1 ? 'rotate(90deg)' : 'none', transition: '0.2s' }} />
                    </button>
                    {openFaq === 1 && (
                      <div style={{ padding: '12px 14px', fontSize: '0.75rem', color: '#475569', lineHeight: 1.5, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
                        Sim. Na aba "Perfil" ou diretamente na timeline do aluno, haverá a opção de anexar justificativas para ausências (atestados, atestados médicos, etc.) para análise da secretaria da escola.
                      </div>
                    )}
                  </div>

                  {/* FAQ 3 */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                    <button 
                      onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                      style={{ width: '100%', padding: '12px 14px', background: '#f8fafc', border: 'none', textAlign: 'left', fontWeight: 700, fontSize: '0.8rem', color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    >
                      <span>Por que não recebo notificações no celular?</span>
                      <ChevronRight size={14} style={{ transform: openFaq === 2 ? 'rotate(90deg)' : 'none', transition: '0.2s' }} />
                    </button>
                    {openFaq === 2 && (
                      <div style={{ padding: '12px 14px', fontSize: '0.75rem', color: '#475569', lineHeight: 1.5, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
                        Verifique se você permitiu as notificações nas configurações do PWA ou do seu navegador. O app precisa de autorização de notificações do sistema operacional Android/iOS para exibir alertas flutuantes.
                      </div>
                    )}
                  </div>

                </div>

                {/* Support Contact */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                  <a 
                    href="tel:08007772026"
                    style={{
                      padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                    }}
                  >
                    <Phone size={16} style={{ color: '#ea580c' }} />
                    Falar no Suporte: 0800 777 2026
                  </a>

                  <a 
                    href="mailto:suporte@alunoconectado.com.br"
                    style={{
                      padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                    }}
                  >
                    <Mail size={16} style={{ color: '#ea580c' }} />
                    suporte@alunoconectado.com.br
                  </a>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
      
      {/* Local keyframe styling for slide up animation */}
      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
